import React from "react";
import PageFrame from "./PageFrame";
import Abs from "./Abs";
import {STRIVE_DOMAIN, COVER_FOOT_TOP, COVER_FOOT_TOP_FALLBACK} from "../bov-templates";

/**
 * Page 01 - full bleed cover image with the deal's identity on its foot bar.
 *
 * The artwork carries the STRIVE mark and the graded foot bar; everything written on the bar
 * is drawn here. That is the difference from the back cover, which has its address and
 * STRIVEre.com baked in: the five cover files are shared between Houston and Dallas, keyed by
 * product and tenancy, so anything office-specific in the artwork would mean ten covers
 * instead of five.
 *
 * The bar names **the property**, in two blocks that sit against the content margins:
 *
 *     Pricing Analysis & Marketing Proposal        402 E Main St, Canton, OK 73724
 *     Family Dollar                                                   STRIVEre.com
 *
 * It used to carry a contact strip instead - agent, the STRIVE office address, STRIVEre.com,
 * agent, divided by three green rules and matching the back cover cell for cell. The business
 * replaced that on 2026-09-12: a cover should say which property the proposal is for, and the
 * office address said nothing a reader of a Dallas deck did not already know. The agents and
 * the office address are still on the back cover, so nothing was lost from the deck.
 *
 * **The cover prints no page number.** `pageNo` is deliberately not passed on, so PageFrame
 * renders none - the same arrangement the back cover uses, and for the same reason: the
 * number sat on the bar the property name now occupies. `dark` went with it, since the only
 * rule it ever selected was the light page-number colour. The page still counts towards the
 * deck's `totalPages`; only its number is suppressed.
 */

/** The eyebrow above the property name. The deck's own title, the same on every cover. */
const DECK_TITLE = 'Pricing Analysis & Marketing Proposal';

/**
 * The two blocks, in page pixels on the 1056 x 816 page.
 *
 * `FOOT_GAP` is the clear space between the top edge of the graded bar and the first line of
 * text, and it is measured down from each cover's own bar rather than from the page, so every
 * deck prints the same gap. The bar starts 9px lower on retail single-tenant than on the
 * other four (see COVER_FOOT_TOP), so a fixed y gave that deck almost no gap while the others
 * had a usable one - the two did not line up, which is what this fixes.
 *
 * 20px was chosen against the shortest bar, retail single-tenant's: it is the one with the
 * least room to give, and at 20 it still clears 18px beneath the blocks. Every other cover
 * has a taller bar and so ends up with more space below, never less above.
 *
 * The blocks run wider than the deck's 48/1008 content margins, to 34 and 1022. The bar is
 * full bleed and carries only these two blocks, so the usual margins buy nothing here and the
 * 28px they cost is 28px of property name. `FOOT_GUTTER` keeps the name clear of the address
 * when both are long.
 */
const FOOT_GAP = 20;
const FOOT_H = 76;
const FOOT_LEFT = 34;
const FOOT_RIGHT = 1022;
const FOOT_GUTTER = 16;
const RIGHT_W = 400;
const RIGHT_X = FOOT_RIGHT - RIGHT_W;
const LEFT_W = RIGHT_X - FOOT_GUTTER - FOOT_LEFT;

/**
 * The property name is set to whatever size makes it fit on one line, down to a floor.
 *
 * It used to be a flat 34px, which a long name outgrew: "Freddy's Frozen Custard &
 * Steakburgers" wrapped to two lines, and two 40px lines under a 25px eyebrow is 105px in a
 * 76px block, so the name dropped below the bar and the page's `overflow: hidden` swallowed
 * it. A cover printed with no property name on it at all.
 *
 * The size is estimated from the character count rather than measured, because the deck is
 * laid out for print and there is no layout to measure at render. `NAME_ADVANCE` is the
 * average glyph advance as a fraction of the font size, taken against the reference cover's
 * "Family Dollar" and rounded up, so the estimate errs towards a size that is slightly too
 * small rather than one that overflows. The stylesheet's ellipsis is the backstop if a name
 * defeats even the floor.
 *
 * It was 0.56 while the name was set in the regular weight. The business asked for bold on
 * 2026-09-17, and bold glyphs carry more advance, so the figure rose with it - left at 0.56
 * the estimate would run long names a little past the block it is meant to keep them inside.
 */
const NAME_SIZE_MAX = 34;
const NAME_SIZE_MIN = 20;
const NAME_ADVANCE = 0.58;

function nameFontSize(name) {
    const chars = String(name || '').trim().length;
    if (!chars) return NAME_SIZE_MAX;
    const fitted = LEFT_W / (chars * NAME_ADVANCE);
    // Half-pixel steps: fine enough that the drop is not visible between similar names,
    // coarse enough that two decks of the same property cannot render a hair apart.
    return Math.max(NAME_SIZE_MIN, Math.min(NAME_SIZE_MAX, Math.floor(fitted * 2) / 2));
}

/**
 * The address, as one line where it fits and two where it does not.
 *
 * The reference prints it on one - "402 E Main St, Canton, OK 73724" - and short addresses
 * still do. A longer one used to wrap wherever the box ran out, which broke it at no
 * meaningful point: "4385 S Westmoreland Rd, Dallas, TX" with a lone "75237" beneath. It now
 * breaks between the street and the town, so the second line is always "City, State ZIP".
 *
 * `ADDRESS_ADVANCE` is the average glyph advance as a fraction of the font size, the same
 * estimate the property name uses, calibrated so "2111 Houston Hwy, Victoria, TX 77901" still
 * prints on one line and the Westmoreland address does not.
 *
 * Built up in pieces so a property missing any field still reads correctly: the comma before
 * the town and the space before the ZIP belong to the part that follows, so neither is left
 * stranded when that part is blank. The underwriting requires none of these fields, and a
 * cover with a trailing comma on it would go out to a client.
 */
const ADDRESS_ADVANCE = 0.5;
const ADDRESS_SIZE = 21;

function addressLines({address, city, state, zip} = {}) {
    const town = [city, state].filter(Boolean).join(', ');
    const locality = [town, zip].filter(Boolean).join(' ');
    const oneLine = [address, locality].filter(Boolean).join(', ');
    if (!oneLine) return [];

    const fits = oneLine.length * ADDRESS_ADVANCE * ADDRESS_SIZE <= RIGHT_W;
    if (fits || !address || !locality) return [oneLine];
    return [address, locality];
}

export default function Cover({template, property}) {
    const {name} = property || {};
    const footY = (COVER_FOOT_TOP[template.cover] || COVER_FOOT_TOP_FALLBACK) + FOOT_GAP;

    return (
        <PageFrame bleed>
            <img className='bov-cover__img' src={template.cover} alt='' loading='eager'/>

            <Abs x={FOOT_LEFT} y={footY} w={LEFT_W} h={FOOT_H}>
                <div className='bov-cover__foot'>
                    <div className='bov-cover__eyebrow'>{DECK_TITLE}</div>
                    <div className='bov-cover__name' style={{fontSize: `${nameFontSize(name)}px`}}>
                        {name}
                    </div>
                </div>
            </Abs>

            <Abs x={RIGHT_X} y={footY} w={RIGHT_W} h={FOOT_H}>
                <div className='bov-cover__foot bov-cover__foot--right'>
                    {/* Wrapped so the address counts as one flex item however many lines it
                        takes: the block is `space-between`, and without this a two-line
                        address would push STRIVEre.com up into it instead of leaving it on
                        the foot of the bar beside the property name. */}
                    <div>
                        {addressLines(property).map(line => (
                            <div key={line} className='bov-cover__address'>{line}</div>
                        ))}
                    </div>
                    <div className='bov-cover__address'>{STRIVE_DOMAIN}</div>
                </div>
            </Abs>
        </PageFrame>
    );
}
